// The one cap on the device pixel ratio a raster pays for, the one reader
// of the ratio, and the render scale of the engine surface.
//
// The engine surface, the menu previews and the arcade deck each draw at
// the device ratio, and each one stops at the same figure: a telephone
// with a ratio of 3 must not draw nine pixels for each one it shows, and a
// retina thumb is already more pixels than the picture carries. The three
// used to spell the figure apart. It lives in a leaf that imports nothing,
// so a pure render module and a page with no engine read it alike.

/** The highest device pixel ratio a canvas rasterises at. */
export const MAX_DPR = 2;

/**
 * The device pixel ratio, at most `cap`. A host with no window, or one
 * that reports no positive ratio, reads as 1. The ratio is not floored at
 * 1: a page zoomed out reports less than 1, and a caller that wants the
 * floor writes `Math.max(1, deviceRatio())`.
 */
export function deviceRatio(cap = Infinity): number {
  const raw = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
  return Math.min(cap, raw);
}

/**
 * The render scale in force (R484 item 2, ui-map-interface.md §10.16): the
 * share of the capped device ratio the engine surface draws at, 1 for the
 * map as it is drawn. The setting `renderScale` of `ui/settings.ts` writes
 * it on the three engine pages; every other page keeps 1, so a reel, a lab
 * and the visual diff draw at the ratio the query states.
 */
let scale = 1;
const scaleListeners = new Set<() => void>();

/** The render scale in force: a share in (0, 1]. */
export function renderScale(): number { return scale; }

/**
 * Applies a render scale. A share that is not in (0, 1] is 1. A change
 * tells every `onRenderScale` listener, so each surface sizes its backing
 * store again at once.
 */
export function setRenderScale(share: number): void {
  const next = Number.isFinite(share) && share > 0 && share <= 1 ? share : 1;
  if (next === scale) return;
  scale = next;
  for (const fn of [...scaleListeners]) fn();
}

/** Calls `fn` after each change of the render scale; returns the function
 *  that removes it. */
export function onRenderScale(fn: () => void): () => void {
  scaleListeners.add(fn);
  return () => { scaleListeners.delete(fn); };
}
